// Pager: turning the pages of a long list. `full` is a recessed well holding First, Previous,
// the page it is on, Next and Last as raised keys, with the rows showing beside it; `mini` is
// the same well with chevrons round "3 of 12", for tight spots. Arrow keys, Home and End turn
// the pages from any key. One page has nothing to turn, so the pager grows in only once there
// is a second. `Paged` pages a list the page already holds; `LoadMore` extends a list instead.
// docs/operations/design-standard.md, "Controls".

import React, { useState } from 'react';
import Button from './Button';
import Reveal from './Reveal';
import Tooltip from './Tooltip';
import { IconChevronLeft, IconChevronRight } from './icons';
import { CONTROL_MOTION, FOCUS, PRESS, STATE, TOUCH, useTicker } from './motion';

export const PAGE_SIZE = 25;

export type PagerSize = 'full' | 'mini';

export interface PagerProps {
  /** The page showing, counted from 0. */
  page: number;
  /** How many pages there are. */
  pageCount: number;
  onPage: (page: number) => void;
  /** Rows across every page. With `pageSize`, a full pager says which are showing: "51 to 75 of 83". */
  total?: number;
  pageSize?: number;
  size?: PagerSize;
  /** Names the page controls, so two pagers on one screen are told apart: "Pages of drones". */
  label?: string;
  /** The next page is loading (paging on the server): turning holds until it lands, so a second press never skips one. */
  busy?: boolean;
  /** Stays on screen with a single page. */
  showSingle?: boolean;
  align?: 'start' | 'center' | 'end';
  className?: string;
}

type Move = 'first' | 'previous' | 'next' | 'last';

const NAME: Record<Move, string> = { first: 'First page', previous: 'Previous page', next: 'Next page', last: 'Last page' };
const WORD: Record<Move, string> = { first: 'First', previous: 'Previous', next: 'Next', last: 'Last' };
const KEYBOARD: Record<Move, string> = { first: 'Home', previous: 'Left arrow', next: 'Right arrow', last: 'End' };
const SPENT: Record<Move, string> = {
  first: 'Already on the first page',
  previous: 'Already on the first page',
  next: 'Already on the last page',
  last: 'Already on the last page',
};
const BY_KEY: Record<string, Move> = { ArrowLeft: 'previous', ArrowRight: 'next', Home: 'first', End: 'last' };
const JUSTIFY = { start: 'justify-start', center: 'justify-center', end: 'justify-end' } as const;

/** A raised key in the well. Spent or held, it sinks flat into the track. */
const keyClass = (size: PagerSize, idle: boolean): string =>
  `inline-flex select-none items-center justify-center whitespace-nowrap rounded-inner font-medium ${
    size === 'mini' ? 'h-6 w-6' : 'h-7 px-2.5 text-[13px]'
  } ${
    idle
      ? 'cursor-default bg-transparent text-on-surface-disabled'
      : `bg-surface-container-highest text-on-surface shadow-key ${STATE} ${PRESS}`
  } ${CONTROL_MOTION} ${FOCUS} ${TOUCH}`;

export const Pager: React.FC<PagerProps> = ({
  page, pageCount, onPage, total, pageSize, size = 'full', label = 'Pages', busy = false, showSingle = false,
  align = 'start', className = '',
}) => {
  const pages = Math.max(1, pageCount);
  const at = Math.min(Math.max(0, page), pages - 1);
  const shown = useTicker(at + 1);

  const target = (m: Move): number => (m === 'first' ? 0 : m === 'previous' ? at - 1 : m === 'next' ? at + 1 : pages - 1);
  const spent = (m: Move): boolean => (m === 'first' || m === 'previous' ? at <= 0 : at >= pages - 1);

  const go = (m: Move) => {
    if (busy || spent(m)) return;
    onPage(target(m));
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const m = BY_KEY[e.key];
    if (!m) return;
    e.preventDefault();
    go(m);
  };

  const moves: Move[] = size === 'mini' ? ['previous', 'next'] : ['first', 'previous', 'next', 'last'];
  const key = (m: Move) => {
    const off = spent(m);
    const idle = off || busy;
    return (
      <Tooltip key={m} label={NAME[m]} note={off ? SPENT[m] : busy ? 'Loading this page' : `${KEYBOARD[m]} from any page key`}>
        <button
          type="button"
          aria-label={NAME[m]}
          // Never `disabled`: a spent key keeps its name and its focus, so Next can be pressed to the end.
          aria-disabled={idle ? true : undefined}
          onClick={() => go(m)}
          className={keyClass(size, idle)}
        >
          {size === 'mini' ? (m === 'previous' ? <IconChevronLeft /> : <IconChevronRight />) : WORD[m]}
        </button>
      </Tooltip>
    );
  };

  const widest = size === 'mini' ? `${pages} of ${pages}` : `Page ${pages} of ${pages}`;
  const range = size === 'full' && total !== undefined && pageSize ? { start: Math.min(total, at * pageSize + 1), end: Math.min(total, (at + 1) * pageSize) } : null;

  return (
    <Reveal show={showSingle || pages > 1}>
      {/* Room inside the reveal's clip for the keys' focus rings and their touch slop. */}
      <div className={`flex py-1.5 ${JUSTIFY[align]} ${className}`}>
        <nav aria-label={label} aria-busy={busy || undefined} onKeyDown={onKeyDown} className="flex min-w-0 items-center gap-3">
          <div className="inline-flex flex-none items-center gap-0.5 rounded-control bg-surface-container-high p-0.5 shadow-recess">
            {moves.slice(0, moves.length / 2).map(key)}
            {/* The widest reading is held by a pseudo-element, so it is not a second node of text. */}
            <span aria-hidden="true" data-w={widest} className={`grid whitespace-nowrap tabular-nums text-on-surface-variant after:invisible after:col-start-1 after:row-start-1 after:content-[attr(data-w)] ${size === 'mini' ? 'px-1.5 text-xs' : 'px-2.5 text-[13px]'}`}>
              <span className="col-start-1 row-start-1 text-center">
                {size === 'full' && 'Page '}<span className="font-medium text-on-surface">{shown}</span> of {pages}
              </span>
            </span>
            {moves.slice(moves.length / 2).map(key)}
          </div>
          <span className="sr-only" aria-live="polite" aria-atomic="true">Page {at + 1} of {pages}</span>
          {range && (
            <span data-w={`${total} to ${total} of ${total}`} className="grid flex-none whitespace-nowrap text-xs tabular-nums text-on-surface-variant after:invisible after:col-start-1 after:row-start-1 after:content-[attr(data-w)]">
              <span className="col-start-1 row-start-1">{range.start} to {range.end} of {total}</span>
            </span>
          )}
        </nav>
      </div>
    </Reveal>
  );
};

export interface PagedProps<T> {
  items: T[];
  children: (page: T[]) => React.ReactNode;
  /** Changing this sends the pager back to the first page; a length change only clamps. */
  resetKey?: string | number;
  /** Names the page controls for a reader when more than one pager is on screen. */
  label?: string;
  pageSize?: number;
  size?: PagerSize;
  align?: 'start' | 'center' | 'end';
  /**
   * Bring one item into view: while this matches an item that is not on the current
   * page, the pager jumps to its page (a banner's "Show it" points at a row that may sit
   * on page three). Pass undefined once it has been shown.
   */
  reveal?: (item: T) => boolean;
}

/**
 * Pages a list the page already holds in full. It owns the page index and hands its children
 * only the visible slice, so a caller that renders several lists needs no hook per list.
 * The index goes back to the first page only when `resetKey` changes; a list that merely
 * changes length keeps its page, clamped to the last that exists, because a polled queue or a
 * row answered in place should never throw someone back to page one. The reset happens during
 * render, not in an effect, so a shrunk list never paints one frame of an empty page.
 */
export function Paged<T>({ items, children, resetKey, label = 'Pages', pageSize = PAGE_SIZE, size = 'full', align = 'end', reveal }: PagedProps<T>) {
  const [page, setPage] = useState(0);
  const [seenKey, setSeenKey] = useState(resetKey);
  if (seenKey !== resetKey) {
    setSeenKey(resetKey);
    setPage(0);
  }

  const n = items.length;
  const pages = Math.max(1, Math.ceil(n / pageSize));
  let current = Math.min(page, pages - 1);
  if (reveal) {
    const found = items.findIndex(reveal);
    const want = found >= 0 ? Math.floor(found / pageSize) : -1;
    if (want >= 0 && want !== current) {
      setPage(want);
      current = want;
    }
  }
  const start = current * pageSize;

  return (
    <>
      {children(n > pageSize ? items.slice(start, start + pageSize) : items)}
      <Pager
        page={current}
        pageCount={pages}
        onPage={setPage}
        total={n}
        pageSize={pageSize}
        size={size}
        label={label}
        align={align}
        className="pt-3"
      />
    </>
  );
}

export interface LoadMoreProps {
  /** More rows exist. When it turns false the button dissolves. */
  more: boolean;
  /** The next rows are on their way: presses are held, and the label says so. */
  busy?: boolean;
  onMore: () => void;
  label?: string;
  className?: string;
}

/** Extends a list in place instead of paging it: for a ledger read top to bottom. */
export const LoadMore: React.FC<LoadMoreProps> = ({ more, busy = false, onMore, label = 'Load more', className = '' }) => (
  <Reveal show={more}>
    <div className={`flex justify-center py-1.5 ${className}`}>
      <Tooltip label={busy ? 'Loading the next rows' : 'Add the next rows under these'}>
        <Button
          variant="tonal"
          aria-disabled={busy || undefined}
          aria-busy={busy || undefined}
          onClick={() => { if (!busy) onMore(); }}
        >
          {/* Both labels share one cell, so the key keeps its width while the words swap. */}
          <span className="grid">
            <span aria-hidden="true" className="invisible col-start-1 row-start-1">{label}</span>
            <span aria-hidden="true" className="invisible col-start-1 row-start-1">Loading</span>
            <span className="col-start-1 row-start-1">{busy ? 'Loading' : label}</span>
          </span>
        </Button>
      </Tooltip>
    </div>
  </Reveal>
);

export default Pager;
